import React from 'react';
import loadable from '@loadable/component';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

// Comprehensive Terms of Service for Merrily
const fallbackTerms = `
*Last updated: March 2026*

## Welcome to Merrily

These Terms of Service ("Terms") govern your use of the Merrily platform operated by VocalMist LLC ("we," "our," or "us"). By accessing or using our services, you agree to be bound by these Terms.

## 1. Platform Overview

### Our Role
Merrily is a marketplace that connects party kit providers ("Providers") with renters ("Renters"). We are **NOT** a party supply company. We facilitate peer-to-peer transactions between users but do not own, sell, or rent party supplies directly.

### Your Responsibilities
You are responsible for all interactions, transactions, and communications with other users. We provide the platform but are not a party to the rental agreements between Providers and Renters.

## 2. User Accounts

- You must be at least 18 years old to use Merrily
- You must provide accurate and complete information when creating your account
- You are responsible for maintaining the security of your account
- We reserve the right to terminate accounts that violate these Terms or engage in prohibited conduct

## 3. Listings and Content

### Provider Responsibilities
- Provide accurate descriptions, photos, and pricing for your party kits
- Ensure all items are clean, safe, and in good working condition
- Respond promptly to rental requests and communications
- Maintain proper insurance coverage for your rental items

### Content Standards
- All content must be truthful and not misleading
- Photos must accurately represent the actual items available for rent
- We reserve the right to remove listings that violate our standards

## 4. Bookings and Payments

### Payment Processing
- All payments are processed through Stripe, our payment processor
- Renters pay an 8% customer service fee on each transaction
- Providers pay a 10% commission on successful rentals
- We do not store credit card information

### Cancellation Policy
- More than 7 days before pickup: Full refund
- 3-7 days before pickup: 75% refund  
- 24-72 hours before pickup: 50% refund
- Less than 24 hours before pickup: No refund

## 5. Handoff Verification

### Documentation Requirements
Both parties are responsible for documenting item condition during pickup and return. This includes:
- Photos of all items as specified in the listing
- Verification of item completeness and condition
- Immediate reporting of any discrepancies

### Dispute Evidence
Photos taken during handoff serve as primary evidence for resolving disputes. Both parties should ensure all required documentation is completed before finalizing pickup or return.

## 6. Security Deposits and Damage Claims

### Security Deposits
- Refundable security deposits may be required and are held during the rental period
- Deposits are automatically released upon successful return of items

### Damage Claims Process
- Providers may open damage claims within 48 hours of item return
- Claims must include photographic evidence and detailed descriptions
- VocalMist LLC reviews all evidence and makes final determinations on claims
- Our decision on damage claims under $500 is final and binding

## 7. Delivery Services

### Third-Party Delivery
- Delivery services may be provided through third-party providers like Roadie
- We are not liable for damage, delays, or losses during delivery
- Delivery provider insurance policies apply to delivery-related issues
- All delivery arrangements are between users and delivery providers

## 8. Prohibited Items and Activities

### Prohibited Items
- Illegal items or substances
- Weapons of any kind
- Hazardous materials
- Items requiring special permits or licenses
- Stolen or counterfeit goods

### Prohibited Conduct
- Fraudulent or deceptive practices
- Harassment or threatening behavior
- Attempting to circumvent platform fees
- Using the platform for illegal purposes

## 9. Liability and Insurance

### Platform Liability Limitations
- Merrily provides a marketplace platform only
- We are not liable for disputes between users
- We are not liable for property damage, personal injury, or other losses arising from rentals
- Our total liability is limited to the fees you paid to us

### User Insurance Requirements
- Providers should maintain appropriate insurance for their rental items
- Renters should verify their own insurance coverage for rented items
- Platform-provided protection is limited to our dispute resolution process

## 10. Dispute Resolution

### Internal Resolution Process
1. Users are encouraged to resolve disputes directly
2. VocalMist LLC will review disputes involving damage claims or policy violations
3. We will base decisions on available evidence including handoff photos and communications
4. Our decisions on amounts under $500 are final

### Arbitration for Larger Disputes
- Disputes exceeding $500 may be subject to binding arbitration
- Arbitration will be conducted under the rules of the American Arbitration Association
- Legal proceedings must be brought in Santa Barbara County, California

## 11. Intellectual Property

### User Content License
By uploading content to our platform, you grant us a worldwide, non-exclusive license to use, display, and distribute your content for platform operation purposes.

### Platform Protection
"Merrily" and related branding are protected intellectual property. Users may not use our branding without explicit permission.

## 12. Modifications to Terms

We may update these Terms periodically. We will notify users of material changes via email or platform notification. Continued use after changes constitutes acceptance of updated Terms.

## 13. Contact Information

For questions about these Terms or our services:

**VocalMist LLC**  
Email: [legal@rentmerrily.com](mailto:legal@rentmerrily.com)  
Phone: (805) 555-0123  

By using Merrily, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
`;

// Create fallback content (array of sections) in page asset format:
export const fallbackSections = {
  sections: [
    {
      sectionType: 'article',
      sectionId: 'terms',
      appearance: { fieldType: 'customAppearance', backgroundColor: '#ffffff' },
      title: { fieldType: 'heading1', content: 'Terms of Service' },
      blocks: [
        {
          blockType: 'defaultBlock',
          blockId: 'hero-content',
          text: {
            fieldType: 'markdown',
            content: fallbackTerms,
          },
        },
      ],
    },
  ],
  meta: {
    pageTitle: {
      fieldType: 'metaTitle',
      content: 'Terms of service page',
    },
    pageDescription: {
      fieldType: 'metaDescription',
      content: 'Terms of service fetch failed',
    },
  },
};

// This is the fallback page, in case there's no Terms of Service asset defined in Console.
const FallbackPage = props => {
  return <PageBuilder pageAssetsData={fallbackSections} {...props} />;
};

export default FallbackPage;
